import React, { useState, useRef, useEffect, useContext } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import { DirectionContext } from '../Contexts/DirectionContext';
import { Trans } from 'react-i18next';
import MoreBlogs from './MoreBlogs';
import WOW from 'wowjs';
import { useBlog } from '../Contexts/BlogContext';
import { contentData } from './FullBlogs';

export default function FullBlog() {
    // All hooks at the top
    const { state } = useLocation();
    const { setCurrentBlog } = useBlog();
    const { id } = useParams();
    const { dir } = useContext(DirectionContext);
    const lang = dir === 'rtl' ? 'ar' : 'en';
    const [activeTab, setActiveTab] = useState("introduction");
    const contentRef = useRef(null);
    const activeContentRef = useRef(null);
    const [isLoading, setIsLoading] = useState(true);

    // Data state
    const [blogData, setBlogData] = useState(null);

    useEffect(() => {
        // Find the blog data from either state (when navigated) or contentData (direct access)
        let foundBlog;
        
        if (state) {
            // When navigated from within app
            foundBlog = state.blogData;
        } else {
            // When accessed directly via URL
            foundBlog = contentData.find(b => b.id === Number(id));
        }

        if (foundBlog) {
            setBlogData(foundBlog);
        } else {
            setBlogData(null);
        }
        
        setIsLoading(false);
    }, [id, state]);

    // Other useEffect hooks remain the same
    useEffect(() => {
        new WOW.WOW({
            live: true,
            mobile: false
        }).init();
    }, []);

    useEffect(() => {
        if (id) {
            setCurrentBlog(Number(id));
            setActiveTab("introduction");
        }
        return () => {
            setCurrentBlog(null);
        };
    }, [id, setCurrentBlog]);

    // Scroll effects remain the same
    useEffect(() => {
        if (contentRef.current) {
            contentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, []);

    useEffect(() => {
        if (!contentRef.current) return;

        if (activeTab === "introduction") {
            contentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }

        const activeElement = document.querySelector(`[data-tab="${activeTab}"]`);
        if (!activeElement) return;

        const scrollPosition = activeElement.offsetTop - contentRef.current.offsetTop - 20;
        contentRef.current.scrollTo({
            top: scrollPosition,
            behavior: 'smooth'
        });
    }, [activeTab]);

    // Conditional rendering after all hooks
    if (isLoading) {
        return <div className="container text-center py-5">Loading...</div>;
    }

    if (!blogData) {
        return (
            <div className="container text-center py-5">
                <h2>Blog not found</h2>
                <Link to="/info/Blogs" className="btn btn-primary mt-3">
                    <Trans i18nKey="BackBtn">{dir === 'ltr' ? 'Back to Blogs' : 'العودة إلى المقالات'}</Trans>
                </Link>
            </div>
        );
    }

    // Tab and content logic
    const TabStyle = {
        borderRight: dir === 'rtl' ? '4px solid rgba(203, 204, 205, 1)' : 'none',
        borderLeft: dir === 'ltr' ? '4px solid rgba(203, 204, 205, 1)' : 'none',
    };

    const tabs = blogData.sections ? Object.keys(blogData.sections).map(key => ({
        id: key,
        label: blogData.sections[key].title[lang]
    })) : [];

    const handleTabClick = (tabId) => {
        setActiveTab(tabId);
    };

    const getContentComponent = (tabId) => {
        if (!blogData.sections || !blogData.sections[tabId]) return null;

        const data = blogData.sections[tabId];
        const content = data.content[lang];

        return (
            <div className="col-md-12 p-3" ref={activeTab === tabId ? activeContentRef : null} data-tab={tabId}>
                <h4 className='PartTitle mb-2'>{data.title[lang]}</h4>
                {Array.isArray(content) ? (
                    typeof content[0] === 'object' ? (
                        <ol>
                            {content.map((item, index) => (
                                <li key={index} className='mb-3'>
                                    <span className='articlePoints'>{item.point}:</span> {item.text}
                                </li>
                            ))}
                        </ol>
                    ) : (
                        <>
                            <p>{content[0]}</p>
                            <ul>
                                {content[1].map((item, index) => (
                                    <li key={index} className='mb-2'>{item}</li>
                                ))}
                            </ul>
                        </>
                    )
                ) : (
                    <p>{content}</p>
                )}
            </div>
        );
    };
    return (
        <div>
            <div className="container-fluid PagesLyout">
                <div className="row">
                    <div className="col-md-12">
                        <Link to="/info/Blogs" className="ReadMoreAnchor">
                            <i className={dir === 'ltr' ? 'fa-solid fa-chevron-left px-2' : 'fa-solid fa-chevron-right px-2'}></i>
                            <Trans i18nKey="BackBtn">{dir === 'ltr' ? 'Back' : 'الرجوع'}</Trans>
                        </Link>
                    </div>
                </div>
                <div className="row justify-content-center mx-3 g-4 my-5">
                    <div className="col-md-11 col-lg-8 col-xl-7 text-center " >
                        <h1 className="Title mb-3 wow fadeInLeft justify-content-center" data-wow-delay="0.3s" >
                            {dir === 'ltr' ? blogData.title_en : blogData.title}
                        </h1>
                        <div className="row justify-content-center" style={{ fontSize: '22px' }}>
                            <div className="col-auto">
                                <p>{dir === 'ltr' ? blogData.date_en : blogData.date}</p>
                            </div>
                            <div className="col-auto p-0">
                                <i className="bi bi-three-dots-vertical"></i>
                            </div>
                            <div className="col-auto">
                                <p>{dir === 'ltr' ? blogData.readTime_en : blogData.readTime}</p>
                            </div>
                        </div>
                    </div>

                </div>

                <div className={`BlogHero mx-lg-4 my-5 ${blogData.imageClass}`}>
                    <div className="Hero-Content-container"></div>
                </div>

                <div className="container-fluid Blog mb-5 px-lg-5">
                    <div className="row">
                        {/* Sidebar Tabs */}
                        <div className="col-lg-3">
                            <div className="row">
                                {tabs.map((tab) => (
                                    <div className="col-md-12" key={tab.id}>
                                        <p
                                            className={`Tab ${activeTab === tab.id ? 'active' : ''}`}
                                            onClick={() => handleTabClick(tab.id)}
                                            style={TabStyle}
                                        >
                                            {tab.label}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Content Section */}
                        <div className="col-lg-9 BContentContainer" ref={contentRef}>
                            <p className='p-3'>{dir === 'ltr' ? blogData.brief_en : blogData.brief}</p>
                            {tabs.map(tab => (
                                <div key={tab.id} className={activeTab === tab.id ? 'ActivePart' : ''}>
                                    {getContentComponent(tab.id)}
                                </div>
                            ))}



                        </div>
                    </div>
                </div>

                <section className='mt-5'>
                    <div className="container-fluid">
                        <div className="row">
                            <MoreBlogs />
                        </div>
                    </div>
                </section>
            </div>
        </div>
    );
}